import React, { useEffect, useState, useMemo, useCallback } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import "./TFTGridDisplay.css";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";
import { CartIcon, DownloadIcon } from "../../components/ActionIcons";
import { IMAGE_COMING_SOON } from "../../imageComingSoon";


const TARGET_CATEGORY_NAME = "TFT and Touch";


const TARGET_SUBCATEGORY_NAME = null;


const FIELD_PATTERNS = {
  partNo: [/part.?no/i, /part.?number/i, /model.?no/i],
  price: [/^price$/i, /selling.?price/i, /^mrp$/i, /^rate$/i],
  stock: [/^stock$/i, /^qty$/i, /quantity/i, /stock.?(qty|quantity|count)/i, /availability/i],
  image: [/image/i, /photo/i, /picture/i],
  description: [/^description$/i, /^desc$/i, /^about$/i, /^summary$/i, /^details$/i],
};

const testKey = (key, patterns) => patterns.some((p) => p.test(key.trim()));

const matchField = (fieldsObj, patterns) => {
  const entry = Object.entries(fieldsObj || {}).find(([key]) => testKey(key, patterns));
  return entry ? entry[1] ?? "" : "";
};


const DEFAULT_PRICE = 500;


const RESERVED_FOR_FILTERS = (key) =>
  testKey(key, FIELD_PATTERNS.partNo) ||
  testKey(key, FIELD_PATTERNS.price) ||
  testKey(key, FIELD_PATTERNS.stock) ||
  testKey(key, FIELD_PATTERNS.image) ||
  testKey(key, FIELD_PATTERNS.description);


const MAX_FILTER_OPTIONS = 20;

const ROWS_PER_PAGE = 9;

const TFTGridDisplay = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]); // array of { id, subCategoryName, ...fieldsMap }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [filters, setFilters] = useState({}); // { fieldKey: [selectedValue, ...] }
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState(""); // "<key>::asc" | "<key>::desc"
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const formatValue = (val) => {
    if (val && typeof val === "object" && typeof val.toDate === "function") {
      return val.toDate().toLocaleDateString("en-GB"); // Firestore Timestamp
    }
    return String(val ?? "");
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Plain overflow:hidden scroll-locking is unsafe for a full-screen
  // position:fixed drawer on iOS Safari (see NavBar.jsx for the full
  // explanation) -- pin body with position:fixed instead and restore the
  // saved scroll offset on close.
  useEffect(() => {
    if (isMobileFilterOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 10) * -1);
      }
    }
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
    };
  }, [isMobileFilterOpen]);

  // ── FETCH: category → subCategories → productData (real schema) ──
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Find the target category doc by name
        const categorySnap = await getDocs(collection(db, "product_categories"));
        const categoryDoc = categorySnap.docs.find(
          (d) => d.data().categoryName === TARGET_CATEGORY_NAME,
        );

        if (!categoryDoc) {
          throw new Error(
            `Category "${TARGET_CATEGORY_NAME}" not found in product_categories. ` +
              `Check TARGET_CATEGORY_NAME at the top of this file.`,
          );
        }

        // 2. Get subcategories (either all, or the one matching TARGET_SUBCATEGORY_NAME)
        const subCatSnap = await getDocs(
          collection(db, "product_categories", categoryDoc.id, "subCategories"),
        );

        const targetSubCatDocs = TARGET_SUBCATEGORY_NAME
          ? subCatSnap.docs.filter(
              (d) => d.data().subCategoryName === TARGET_SUBCATEGORY_NAME,
            )
          : subCatSnap.docs;

        if (targetSubCatDocs.length === 0) {
          throw new Error(
            TARGET_SUBCATEGORY_NAME
              ? `Subcategory "${TARGET_SUBCATEGORY_NAME}" not found under "${TARGET_CATEGORY_NAME}".`
              : `No subcategories found under "${TARGET_CATEGORY_NAME}".`,
          );
        }

        // 3. For each subcategory, read its productData rows directly.
        //    NOTE: there is no "products" collection layer in the real
        //    schema — rows live at subCategories/{subCatId}/productData,
        //    and each row already carries its full spec as a "fields" map
        //    on the doc itself (no further subcollection to merge).
        const productDataSnaps = await Promise.all(
          targetSubCatDocs.map((subCatDoc) =>
            getDocs(
              collection(
                db,
                "product_categories",
                categoryDoc.id,
                "subCategories",
                subCatDoc.id,
                "productData",
              ),
            ),
          ),
        );

        const allProducts = [];
        targetSubCatDocs.forEach((subCatDoc, i) => {
          productDataSnaps[i].forEach((rowDoc) => {
            const rowData = rowDoc.data();
            allProducts.push({
              id: rowDoc.id,
              subCategoryName: subCatDoc.data().subCategoryName || subCatDoc.id,
              ...(rowData.fields || {}), // the actual spec key/value pairs
            });
          });
        });

        setProducts(allProducts);
      } catch (err) {
        console.error("Firestore Error:", err);
        setError(err.message || "Failed to load products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Scroll reveal
  useEffect(() => {
    const els = document.querySelectorAll(
      ".tft-header-content, .tft-image-collage, .tft-filter-bar, .tft-grid-wrapper",
    );
    const reveal = () => {
      els.forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight - 60) {
          el.classList.add("reveal");
        }
      });
    };
    window.addEventListener("scroll", reveal);
    reveal();
    return () => window.removeEventListener("scroll", reveal);
  }, [products]);

  // ── Dynamically discover which keys are worth turning into filters ──
  // (skips partNo/price/stock/image/description — those are handled
  // specially, not as generic filter pills)
  const filterableKeys = useMemo(() => {
    if (products.length === 0) return [];

    const valuesByKey = {};
    products.forEach((p) => {
      Object.entries(p).forEach(([key, val]) => {
        if (key === "id" || key === "subCategoryName") return;
        if (RESERVED_FOR_FILTERS(key)) return;
        if (val === "" || val === null || val === undefined) return;
        if (typeof val === "object") return; // skip Timestamps / nested objects
        if (!valuesByKey[key]) valuesByKey[key] = new Set();
        valuesByKey[key].add(String(val));
      });
    });

    return Object.entries(valuesByKey)
      .filter(([, set]) => set.size > 1 && set.size <= MAX_FILTER_OPTIONS)
      .map(([key]) => key);
  }, [products]);

  const labelFor = (key) =>
    key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/^./, (c) => c.toUpperCase());

  const availableOptions = useMemo(() => {
    const result = {};
    filterableKeys.forEach((key) => {
      const set = new Set();
      products
        .filter((p) =>
          filterableKeys.every((otherKey) => {
            if (otherKey === key) return true;
            const active = filters[otherKey];
            if (!active || active.length === 0) return true;
            return active.includes(String(p[otherKey] ?? ""));
          }),
        )
        .forEach((p) => {
          const v = p[key];
          if (v !== "" && v !== null && v !== undefined) set.add(String(v));
        });

      result[key] = Array.from(set).sort(
        (a, b) => parseFloat(a) - parseFloat(b) || a.localeCompare(b),
      );
    });
    return result;
  }, [products, filters, filterableKeys]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => {
      const current = prev[key] || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [key]: updated };
    });
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearch("");
    setInStockOnly(false);
    setCurrentPage(1);
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      // Blank or non-numeric stock counts as out of stock.
      if (inStockOnly && !(parseFloat(matchField(p, FIELD_PATTERNS.stock)) > 0)) return false;

      const passesFilters = filterableKeys.every((key) => {
        const active = filters[key];
        if (!active || active.length === 0) return true;
        return active.includes(String(p[key] ?? ""));
      });
      if (!passesFilters) return false;

      if (!search.trim()) return true;
      return Object.values(p).some((v) =>
        formatValue(v).toLowerCase().includes(search.toLowerCase()),
      );
    });
  }, [products, filters, search, filterableKeys, inStockOnly]);

  const sortedProducts = useMemo(() => {
    if (!sortBy) return filteredProducts;
    const [key, dir] = sortBy.split("::");
    const sorted = [...filteredProducts];
    sorted.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      const an = parseFloat(av);
      const bn = parseFloat(bv);
      const cmp =
        !isNaN(an) && !isNaN(bn)
          ? an - bn
          : String(av ?? "").localeCompare(String(bv ?? ""));
      return dir === "desc" ? -cmp : cmp;
    });
    return sorted;
  }, [filteredProducts, sortBy]);

  const totalPages = Math.ceil(sortedProducts.length / ROWS_PER_PAGE);
  const currentRows = sortedProducts.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  const isMobile = windowWidth <= 768;
  const maxBtns = isMobile ? 5 : Math.min(totalPages, 14);
  let startPage = Math.max(
    1,
    Math.min(currentPage - Math.floor(maxBtns / 2), totalPages - maxBtns + 1),
  );
  let endPage = Math.min(totalPages, startPage + maxBtns - 1);
  const visiblePages = Array.from(
    { length: Math.max(0, endPage - startPage + 1) },
    (_, i) => startPage + i,
  );

  const hasFilters =
    Object.values(filters).some((arr) => arr && arr.length > 0) || search || inStockOnly;

  const getPartNo = (p) => matchField(p, FIELD_PATTERNS.partNo) || p.id;
  const getPrice = (p) => {
    const raw = matchField(p, FIELD_PATTERNS.price);
    const parsed = parseFloat(raw);
    return raw !== "" && !isNaN(parsed) ? parsed : DEFAULT_PRICE;
  };
  // Tixaru's own Front Image Url / Back Image Url only -- no other
  // image-shaped field, same policy as the rest of the site's product
  // images. "Image Coming Soon" instead of a stock placeholder photo.
  const getImage = (p) => {
    const front = matchField(p, [/front.*image/i]);
    const back = matchField(p, [/back.*image/i]);
    const first = String(front || back || "").split(",")[0]?.trim();
    return first || IMAGE_COMING_SOON;
  };

  const getTitle = (p) =>
    filterableKeys
      .slice(0, 5)
      .map((k) => p[k])
      .filter(Boolean)
      .join(" / ") || getPartNo(p);

  // SEO: this exact heading is duplicated into the loading/error early
  // returns below -- the page previously had no <h1> at all, and the build-time
  // prerenderer (prerender.js) captures whichever of these three branches the
  // headless browser happens to be looking at when it snapshots the page, so
  // the H1 has to survive every one of them, not just the "products loaded"
  // happy path.
  const pageHeading = (
    <h1 className="tft-page-title">
      Industrial TFT LCD Display Modules for OEMs
    </h1>
  );

  if (loading) {
    return (
      <div className="tft-layout">
        <main className="tft-page">
          {pageHeading}
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading display data…</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tft-layout">
        <main className="tft-page">
          {pageHeading}
          <div className="loading-state">
            <p>Couldn't load products: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="tft-layout">
      <main className="tft-page">
        {pageHeading}
        {products.length > 0 ? (
          <div className="tft-content-layout">
            <button
              className="mobile-filter-trigger"
              onClick={() => setIsMobileFilterOpen(true)}
              aria-label="Open filters"
            >
              Filter & Sort
            </button>

            <div
              className={`tft-sidebar-overlay ${isMobileFilterOpen ? "active" : ""}`}
              onClick={() => setIsMobileFilterOpen(false)}
            />

            <aside
              className={`tft-sidebar ${isMobileFilterOpen ? "mobile-open" : ""}`}
            >
              <div className="sidebar-mobile-header">
                <h3>Filters</h3>
                <button
                  className="sidebar-close-btn"
                  onClick={() => setIsMobileFilterOpen(false)}
                  aria-label="Close filters"
                >
                  ✕
                </button>
              </div>

              <div className="sidebar-section">
                <select
                  className="top-sort-dropdown sidebar-sort"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Sort By</option>
                  {filterableKeys.map((key) => (
                    <React.Fragment key={key}>
                      <option value={`${key}::asc`}>
                        {labelFor(key)}: Low → High
                      </option>
                      <option value={`${key}::desc`}>
                        {labelFor(key)}: High → Low
                      </option>
                    </React.Fragment>
                  ))}
                </select>
              </div>

              {filterableKeys.map((key) => (
                <div className="sidebar-section" key={key}>
                  <h3 className="sidebar-title">{labelFor(key)}</h3>
                  <div className="sidebar-pill-group">
                    {(availableOptions[key] || []).map((opt, i) => {
                      const active = (filters[key] || []).includes(opt);
                      return (
                        <button
                          key={i}
                          type="button"
                          className={`sidebar-pill ${active ? "active" : ""}`}
                          onClick={() => handleFilterChange(key, opt)}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {hasFilters && (
                <button className="sidebar-clear-btn" onClick={clearFilters}>
                  Clear All Filters
                </button>
              )}
            </aside>

            <div className="tft-grid-wrapper">
              <div className="tft-search-row">
                <div className="tft-search-container">
                  <svg
                    className="tft-search-icon"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <circle
                      cx="8.5"
                      cy="8.5"
                      r="5.5"
                      stroke="#9CA3AF"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M13 13l3 3"
                      stroke="#9CA3AF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>

                  <input
                    type="text"
                    placeholder="Search displays..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>

                <button
                  type="button"
                  className={`tft-instock-btn${inStockOnly ? " active" : ""}`}
                  aria-pressed={inStockOnly}
                  onClick={() => {
                    setInStockOnly((v) => !v);
                    setCurrentPage(1);
                  }}
                >
                  Show only in-stock items
                </button>
              </div>

              {hasFilters && (
                <div className="active-filters-bar">
                  {Object.entries(filters).flatMap(([key, values]) =>
                    (values || []).map((value) => (
                      <button
                        key={`${key}-${value}`}
                        className="active-filter-chip"
                        onClick={() => handleFilterChange(key, value)}
                      >
                        <strong>{labelFor(key)}</strong>: {value}
                        <span>✕</span>
                      </button>
                    )),
                  )}
                  <button className="clear-all-chip" onClick={clearFilters}>
                    Clear All
                  </button>
                </div>
              )}

              {currentRows.length > 0 ? (
                <div className="tft-grid">
                  {currentRows.map((p) => {
                    const partNo = getPartNo(p);
                    const title = getTitle(p);
                    const price = getPrice(p);
                    const imageUrl = getImage(p);

                    return (
                      <div
                        className="tft-card"
                        key={p.id}
                        onClick={() =>
                          navigate(`/tft/${encodeURIComponent(partNo)}`, {
                            state: p,
                          })
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <div className="tft-card-top">
                          <div className="tft-part-number">{partNo}</div>
                        </div>

                        <div className="tft-card-image">
                          <img src={imageUrl} alt={partNo} loading="lazy" decoding="async" />
                        </div>

                        <div className="tft-card-content">
                          <div className="tft-title-row">
                            <h3>{title}</h3>
                          </div>

                          <div className="tft-card-info">
                            {filterableKeys.slice(0, 4).map((k) =>
                              p[k] ? (
                                <span key={k} className="tft-card-info-item">
                                  {labelFor(k)}: {formatValue(p[k])}
                                </span>
                              ) : null,
                            )}
                          </div>

                          <div className="tft-card-footer">
                            <button
                              className="add-cart-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart({
                                  ...p,
                                  partNo,
                                  price,
                                  quantity: 1,
                                });
                              }}
                            >
                              <CartIcon size={16} className="add-cart-btn-icon" />
                              Add To Cart
                            </button>

                            {partNo ? (
                              <a
                                href={`/assets/Biba Data Sheet/DWG ${encodeURIComponent(
                                  partNo,
                                )}.pdf`}
                                download={`DWG ${partNo}.pdf`}
                                className="datasheet-icon-btn footer-download-btn"
                                title="Download Datasheet"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DownloadIcon size={18} />
                              </a>
                            ) : (
                              <button
                                className="datasheet-icon-btn footer-download-btn disabled"
                                disabled
                              >
                                <DownloadIcon size={18} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-results-grid">
                  No displays match your criteria
                </div>
              )}

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="pg-arrow"
                  >
                    ◀
                  </button>

                  {startPage > 1 && (
                    <>
                      <button onClick={() => setCurrentPage(1)}>1</button>
                      {startPage > 2 && <span className="pg-ellipsis">…</span>}
                    </>
                  )}

                  {visiblePages.map((n) => (
                    <button
                      key={n}
                      className={currentPage === n ? "active" : ""}
                      onClick={() => setCurrentPage(n)}
                    >
                      {n}
                    </button>
                  ))}

                  {endPage < totalPages && (
                    <>
                      {endPage < totalPages - 1 && (
                        <span className="pg-ellipsis">…</span>
                      )}
                      <button onClick={() => setCurrentPage(totalPages)}>
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="pg-arrow"
                  >
                    ▶
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="loading-state">
            <p>No products found in this category.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default TFTGridDisplay;